import { useRef, useState, type FormEvent } from 'react';
import { FirebaseError } from 'firebase/app';
import { Eye, EyeOff } from 'lucide-react';
import { Modal } from '../../shared/ui/Modal';
import { Button } from '../../shared/ui/Button';
import { Input } from '../../shared/ui/Input';
import { FormField } from '../../shared/ui/FormField';
import { useReauth } from '../../shared/hooks/useReauth';
import { useToast } from '../../shared/hooks/useToast';
import { useAuth } from './useAuth';
import { useSettings } from '../settings/useSettings';
import { changeOwnPassword } from '../../shared/lib/callables';
import { PasswordRulesChecklist } from './PasswordRulesChecklist';
import { PasswordGeneratorButton } from './PasswordGeneratorButton';
import { DEFAULT_SECURITY_SETTINGS } from '../../shared/types/settings';
import styles from './ChangePasswordModal.module.css';

type FormState = {
  current: string;
  next: string;
  confirm: string;
  showNext: boolean;
};

export type ChangePasswordModalProps = {
  open: boolean;
  onClose: () => void;
  /** Quando `true`, esconde botão "Cancelar" (uso do PasswordExpiredGate) */
  blocking?: boolean;
};

function ChangePasswordForm({
  onClose,
  blocking,
}: {
  onClose: () => void;
  blocking: boolean;
}) {
  const toast = useToast();
  const { currentUser, userDoc } = useAuth();
  const { settings } = useSettings();
  const { reauth } = useReauth();
  const formRef = useRef<HTMLFormElement | null>(null);
  const [form, setForm] = useState<FormState>({ current: '', next: '', confirm: '', showNext: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const policy = settings?.security ?? DEFAULT_SECURITY_SETTINGS;
  const context = { name: userDoc?.name, email: userDoc?.email ?? currentUser?.email ?? undefined };
  const { current, next, confirm: confirmField, showNext } = form;

  function patch(p: Partial<FormState>) {
    setForm((prev) => ({ ...prev, ...p }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    if (!current) {
      setError('Informe sua senha atual.');
      return;
    }
    if (next !== confirmField) {
      setError('Confirmação não bate com a nova senha.');
      return;
    }

    setSubmitting(true);
    try {
      await reauth(current);
      await changeOwnPassword({ newPassword: next });
      toast.success('Senha alterada. Sessões em outros dispositivos foram encerradas.');
      onClose();
    } catch (e) {
      console.error('[change-password] error', e);
      if (e instanceof FirebaseError) {
        if (e.code === 'auth/wrong-password' || e.code === 'auth/invalid-credential') {
          setError('Senha atual incorreta.');
        } else if (e.code === 'functions/invalid-argument') {
          const details = (
            e.customData as { details?: { code?: string; failures?: string[] } } | undefined
          )?.details;
          if (details?.code === 'REUSED_PASSWORD') {
            setError('Esta senha já foi usada recentemente. Escolha uma diferente.');
          } else if (details?.code === 'POLICY_VIOLATION') {
            setError('A senha não atende às regras da política. Veja o checklist abaixo.');
          } else {
            setError(e.message);
          }
        } else if (e.code === 'functions/failed-precondition') {
          setError('Sessão expirada. Faça login novamente para alterar a senha.');
        } else {
          setError(e.message);
        }
      } else {
        setError('Erro inesperado. Tente novamente em instantes.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  function triggerSubmit() {
    formRef.current?.requestSubmit();
  }

  return (
    <Modal
      open
      onClose={() => !submitting && !blocking && onClose()}
      title="Trocar senha"
      size="md"
      blocking={blocking}
      footer={
        <>
          {!blocking ? (
            <Button variant="ghost" onClick={onClose} disabled={submitting}>
              Cancelar
            </Button>
          ) : null}
          <Button onClick={triggerSubmit} isLoading={submitting}>
            Trocar senha
          </Button>
        </>
      }
    >
      <form ref={formRef} onSubmit={handleSubmit} className={styles.form}>
        <FormField label="Senha atual" required>
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => patch({ current: e.target.value })}
              disabled={submitting}
              autoFocus
            />
          )}
        </FormField>

        <FormField label="Nova senha" required>
          {(id) => (
            <div className={styles.row}>
              <Input
                id={id}
                type={showNext ? 'text' : 'password'}
                autoComplete="new-password"
                value={next}
                onChange={(e) => patch({ next: e.target.value })}
                disabled={submitting}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => patch({ showNext: !showNext })}
                    aria-label={showNext ? 'Ocultar senha' : 'Mostrar senha'}
                    className={styles['eye-btn']}
                  >
                    {showNext ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                }
              />
              <PasswordGeneratorButton
                disabled={submitting}
                onGenerate={(pwd) => patch({ next: pwd, confirm: pwd, showNext: true })}
              />
            </div>
          )}
        </FormField>

        <div className={styles.checklist}>
          <PasswordRulesChecklist password={next} policy={policy} context={context} />
        </div>

        <FormField label="Confirmar nova senha" required>
          {(id) => (
            <Input
              id={id}
              type={showNext ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirmField}
              onChange={(e) => patch({ confirm: e.target.value })}
              disabled={submitting}
            />
          )}
        </FormField>

        {error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}

        <p className={styles.notice}>
          Sessões em outros dispositivos serão encerradas após a troca.
        </p>
      </form>
    </Modal>
  );
}

export function ChangePasswordModal({ open, onClose, blocking = false }: ChangePasswordModalProps) {
  if (!open) return null;
  return <ChangePasswordForm key={open ? 'open' : 'closed'} onClose={onClose} blocking={blocking} />;
}
